"use client";

import React, { useState, useRef, useEffect } from "react";
import * as S from "./styles";
import LabelInput from "@/components/label";

interface Option {
  value: string | number;
  // label can be a string or a React node for richer rendering
  label: React.ReactNode;
  // plain text used for search/filtering and for setting the input value
  labelText?: string;
  // optional group identifier for grouping options
  group?: string;
}

interface SelectWithSearchProps {
  label?: string;
  placeholder?: string;
  options: Option[];
  name?: string;
  onChange?: (value: string | number) => void;
  value?: string | number;
  disabled?: boolean;
}

const SelectWithSearch: React.FC<SelectWithSearchProps> = ({
  label,
  placeholder,
  options,
  name,
  onChange,
  value,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<Option | null>(null);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      const matched = options.find(
        (option) => String(option.value) === String(value),
      );
      if (matched) {
        setSelected(matched);
        // use labelText if available, otherwise coerce label to string
        const text =
          matched.labelText ??
          (typeof matched.label === "string" ? matched.label : "");
        setSearchValue(text);
      } else {
        setSelected(null);
        setSearchValue("");
      }
    } else {
      setSelected(null);
      setSearchValue("");
    }
  }, [value, options]);

  useEffect(() => {
    if (!value || value === "") {
      setSelected(null);
      setSearchValue("");
    }
  }, [value]);

  const filteredOptions =
    searchValue.trim() === ""
      ? options
      : options.filter((option) => {
          const searchLower = searchValue.toLowerCase();
          const labelText = (
            option.labelText ??
            (typeof option.label === "string" ? option.label : "")
          ).toLowerCase();

          const searchWords = searchLower
            .split(" ")
            .filter((word) => word.length > 0);

          if (/\d/.test(searchLower)) {
            return labelText.includes(searchLower.replace(/\D/g, ""));
          }

          return (
            searchWords.every((word) => labelText.includes(word)) ||
            labelText.includes(searchLower)
          );
        });

  //   const handleSelect = (option: Option) => {
  //     setSelected(option);
  //     setSearchValue(option.label);
  //     setIsOpen(false);
  //     if (onChange) {
  //       const fakeEvent = {
  //         target: {
  //           name,
  //           value: option.value,
  //         },
  //       } as React.ChangeEvent<HTMLInputElement>;
  //       onChange(fakeEvent);
  //     }
  //   };

  const handleSelect = (option: Option) => {
    setSelected(option);
    const text =
      option.labelText ??
      (typeof option.label === "string" ? option.label : "");
    setSearchValue(text);
    setIsOpen(false);
    if (onChange) {
      onChange(option.value);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSelected(null);
        setSearchValue("");
        if (onChange) {
          onChange("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onChange]);

  return (
    <>
      {label && <LabelInput labelInput={label} />}
      <S.BoxSelect ref={ref} $disabled={disabled}>
        <S.SelectComponent
          as="input"
          type="text"
          name={name}
          value={searchValue}
          autoComplete="off"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onChange={(e) => {
            if (!disabled) {
              setSearchValue(e.target.value);
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
        />
        <S.IconComponent name="chevronDown" />

        {isOpen && !disabled && (
          <S.OptionsList>
            {filteredOptions.length > 0 ? (
              (() => {
                // Agrupa opções por grupo se existir
                const groupedOptions: {
                  [key: string]: Option[];
                } = {};
                const ungroupedOptions: Option[] = [];

                filteredOptions.forEach((option) => {
                  if (option.group) {
                    if (!groupedOptions[option.group]) {
                      groupedOptions[option.group] = [];
                    }
                    groupedOptions[option.group].push(option);
                  } else {
                    ungroupedOptions.push(option);
                  }
                });

                const groupLabels: { [key: string]: string } = {
                  subscription: "Serviço de Assinatura",
                  standalone: "Serviço Avulso",
                };

                const result: React.ReactNode[] = [];

                // Renderiza grupos primeiro
                Object.keys(groupedOptions).forEach((groupKey) => {
                  if (groupedOptions[groupKey].length > 0) {
                    result.push(
                      <S.GroupHeader key={`group-${groupKey}`}>
                        {groupLabels[groupKey] || groupKey}
                      </S.GroupHeader>,
                    );
                    groupedOptions[groupKey].forEach((option, index) => {
                      result.push(
                        <S.OptionItem
                          key={`${groupKey}-${option.value}-${index}`}
                          onClick={() => handleSelect(option)}
                          $isSelected={selected?.value === option.value}
                        >
                          {option.label}
                        </S.OptionItem>,
                      );
                    });
                  }
                });

                // Renderiza opções sem grupo
                ungroupedOptions.forEach((option, index) => {
                  result.push(
                    <S.OptionItem
                      key={`ungrouped-${option.value}-${index}`}
                      onClick={() => handleSelect(option)}
                      $isSelected={selected?.value === option.value}
                    >
                      {option.label}
                    </S.OptionItem>,
                  );
                });

                return result;
              })()
            ) : (
              <S.OptionItem $isDisabled>Nenhuma opção encontrada</S.OptionItem>
            )}
          </S.OptionsList>
        )}
      </S.BoxSelect>
    </>
  );
};

export default SelectWithSearch;
